/**
 * HeaderCustomizationService — CUSTOM HEADER™ (Build 2.05.1).
 *
 * Persists the Roamer Local™ header experience configured through the
 * "Customize Header" modal. One storage payload, one key — no duplicate image
 * system: artwork thumbnails stay the existing static assets; personal photos
 * are stored here (base64 data URLs, capped at 3 per the card's "UP TO 3
 * PHOTOS" contract) exactly like the persona avatar pattern.
 *
 * Persistence: localStorage (immediate) + storageManager.push('operator', …)
 * for cloud sync — the same write-through path OperatorPersonaService uses.
 * Live refresh: the `nomad:header-custom-saved` window CustomEvent (the
 * established UnitsService/OCC pattern) — no polling, no new bus events.
 */

import { storageManager } from '@/services/storage/StorageManager';

export type HeaderImageMode = 'artwork' | 'photos' | 'rotate' | 'specific';
export type HeaderOverlay = 'none' | 'dark' | 'blue' | 'emerald' | 'sunset' | 'auto';
export type HeaderTextVisibility = 'standard' | 'high-contrast';

export interface HeaderCustomization {
  imageMode: HeaderImageMode;
  /** Artwork filename or personal-photo data URL when imageMode='specific'. */
  specificPhoto: string | null;
  /** Uploaded personal photos (data URLs). Hard cap: 3. */
  personalPhotos: string[];
  overlay: HeaderOverlay;
  textVisibility: HeaderTextVisibility;
  showWeather: boolean;
  showCoordinates: boolean;
  showLastUpdated: boolean;
  showScore: boolean;
  expandScoreByDefault: boolean;
  updatedAt: string;
}

const STORAGE_KEY = 'nomad:header:custom:v1';
const CLOUD_DOMAIN = 'operator' as const;
const CLOUD_KEY = 'header-customization';

/** Live-refresh channel (same pattern as `nomad:occ-saved`). */
export const HEADER_CUSTOM_EVENT = 'nomad:header-custom-saved';

export const MAX_PERSONAL_PHOTOS = 3;

/** The existing artwork set — same static assets the card already ships. */
export const ARTWORK_CHOICES = ['header.jpg', 'moment-skyline.jpg', 'moment-lanterns.jpg', 'moment-beach.jpg'];

/** A specific photo must be known artwork or a real data/http URL — else null. */
function sanitizeSpecificPhoto(v: unknown): string | null {
  if (typeof v !== 'string' || v.length === 0) return null;
  if (v.startsWith('data:') || v.startsWith('http')) return v;
  return ARTWORK_CHOICES.includes(v) ? v : null;
}

/** Defaults reproduce the card's existing look exactly (no visual change). */
export function defaultHeaderCustomization(): HeaderCustomization {
  return {
    imageMode: 'rotate',
    specificPhoto: null,
    personalPhotos: [],
    overlay: 'auto',
    textVisibility: 'standard',
    showWeather: true,
    showCoordinates: false,
    showLastUpdated: true,
    showScore: true,
    expandScoreByDefault: false,
    updatedAt: new Date(0).toISOString(),
  };
}

const IMAGE_MODES: HeaderImageMode[] = ['artwork', 'photos', 'rotate', 'specific'];
const OVERLAYS: HeaderOverlay[] = ['none', 'dark', 'blue', 'emerald', 'sunset', 'auto'];

/** Record-by-record sanitize — a corrupt payload degrades to defaults, field by field. */
function sanitize(raw: unknown): HeaderCustomization {
  const d = defaultHeaderCustomization();
  if (raw == null || typeof raw !== 'object') return d;
  const r = raw as Partial<Record<keyof HeaderCustomization, unknown>>;
  return {
    imageMode: IMAGE_MODES.includes(r.imageMode as HeaderImageMode) ? r.imageMode as HeaderImageMode : d.imageMode,
    specificPhoto: sanitizeSpecificPhoto(r.specificPhoto),
    personalPhotos: Array.isArray(r.personalPhotos)
      ? r.personalPhotos.filter((p): p is string => typeof p === 'string').slice(0, MAX_PERSONAL_PHOTOS)
      : [],
    overlay: OVERLAYS.includes(r.overlay as HeaderOverlay) ? r.overlay as HeaderOverlay : d.overlay,
    textVisibility: r.textVisibility === 'high-contrast' ? 'high-contrast' : 'standard',
    showWeather: typeof r.showWeather === 'boolean' ? r.showWeather : d.showWeather,
    showCoordinates: typeof r.showCoordinates === 'boolean' ? r.showCoordinates : d.showCoordinates,
    showLastUpdated: typeof r.showLastUpdated === 'boolean' ? r.showLastUpdated : d.showLastUpdated,
    showScore: typeof r.showScore === 'boolean' ? r.showScore : d.showScore,
    expandScoreByDefault: typeof r.expandScoreByDefault === 'boolean' ? r.expandScoreByDefault : d.expandScoreByDefault,
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : d.updatedAt,
  };
}

export function loadHeaderCustomization(): HeaderCustomization {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return raw != null ? sanitize(JSON.parse(raw)) : defaultHeaderCustomization();
  } catch {
    return defaultHeaderCustomization();
  }
}

function notify(): void {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(HEADER_CUSTOM_EVENT));
  } catch { /* non-browser env */ }
}

export function saveHeaderCustomization(next: Omit<HeaderCustomization, 'updatedAt'>): HeaderCustomization {
  const payload: HeaderCustomization = sanitize({ ...next, updatedAt: new Date().toISOString() });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch { /* quota — cloud push below still records it */ }
  try {
    storageManager.push(CLOUD_DOMAIN, CLOUD_KEY, payload);
  } catch { /* cloud path is best-effort; local state is authoritative */ }
  notify();
  return payload;
}

export function resetHeaderCustomization(): HeaderCustomization {
  return saveHeaderCustomization(defaultHeaderCustomization());
}

/**
 * Add an uploaded personal photo (data URL). Enforces the 3-photo cap by
 * dropping the OLDEST photo — the card's "UP TO 3 PHOTOS" contract.
 */
export function addPersonalPhoto(dataUrl: string): HeaderCustomization {
  const cur = loadHeaderCustomization();
  const photos = [...cur.personalPhotos, dataUrl].slice(-MAX_PERSONAL_PHOTOS);
  return saveHeaderCustomization({ ...cur, personalPhotos: photos });
}

export function removePersonalPhoto(index: number): HeaderCustomization {
  const cur = loadHeaderCustomization();
  const photos = cur.personalPhotos.filter((_, i) => i !== index);
  const specificRemoved = cur.specificPhoto != null && cur.specificPhoto === cur.personalPhotos[index];
  return saveHeaderCustomization({
    ...cur,
    personalPhotos: photos,
    specificPhoto: specificRemoved ? null : cur.specificPhoto,
  });
}

/** Boot-time cloud hydration — only applies a strictly newer cloud copy. */
export async function hydrateHeaderCustomizationFromCloud(): Promise<boolean> {
  try {
    const cloud = await storageManager.hydrate<HeaderCustomization>(CLOUD_DOMAIN, CLOUD_KEY);
    if (!cloud) return false;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitize(cloud)));
    notify();
    return true;
  } catch {
    return false;
  }
}
